import CustomTable from "components/table";
import Config, { SSO_META } from "config";
import dayjs from "dayjs";
import { CustomerInterface } from "interfaces";
import { useEffect, useState } from "react";
import { RootState } from "reducer/store";
import { Card, Header, Modal, Pagination, Popup } from "semantic-ui-react";
import { api, ui, utils } from "services";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { bulkSetUserInfo } from "reducer/user.slice";
import CustomButton from "components/button";
import { Loading } from "components/loading";
import { FacebookShareButton, TelegramShareButton, TwitterShareButton } from "react-share";

export default function ReferralPage() {
    const user = useSelector((state: RootState) => state.user);
    const [referredCustomers, setReferredCustomers] = useState([]);
    const [maxLevel, setMaxLevel] = useState(0);
    const [total, setTotal] = useState(0);
    const [commisionData, setCommission] = useState(10);
    const [limit, setLimit] = useState(5);
    const [page, setPage] = useState(1);
    const [referral, setReferral] = useState("");
    const [isCopy, setIsCopied] = useState(false);
    const [isCopyLink, setIsCopiedLink] = useState(false);
    const [trigger, setTrigger] = useState(false);
    const [totalClaimableCashback, setTotalClaimableCashback] = useState(0)
    const [totalClaimedCashback, setTotalClaimedCashback] = useState(0)
    const [loading, setLoading] = useState(false)
    const [popupShare, openPopupShare] = useState(false)
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [isLoadingReferee, setLoadingReferee] = useState(false);

    const tableHeader = [
        {
            fieldName: "ID",
            className: "",
            fieldKey: "id",
        },
        {
            fieldName: "Name",
            className: "",
            fieldKey: "name",
        },
        {
            fieldName: "Level",
            className: "",
            fieldKey: "level",
        },
    ];

    const claimAllCashback = async () => {
        try {
            setLoading(true);
            await api.claimAllRefereeCashback();
            ui.alertSuccess("Claim MSTR successful !", "Please wait a minute to update MSTR in your wallet !")
        } catch (err: any) {
            ui.alertFailed("Claim MSTR failed !", t(err.message));
        } finally {
            setLoading(false);
            setTrigger(!trigger)
        }
    }

    const submitReferral = async () => {
        try {
            await api.setReferral(referral);
            let rs = await api.post("/dao/get-customer-info", {});
            dispatch(bulkSetUserInfo(rs));
            ui.alertSuccess("Submit code success", '')
        } catch (err: any) {
            ui.alertFailed("Submit code failed", t(err.message))
        }
    }

    useEffect(() => {
        setLoadingReferee(true);
        api.getReferralAnalysis({
            offset: limit * (page - 1),
            limit,
        }).then((data: any) => {
            const { highestLevel, referredCustomerInfos, total, sumTokenTracking, referralCashback, referralCashbackClaimed, commission } = data;
            setReferredCustomers(referredCustomerInfos);
            const ownedCashback = ((referralCashback ?? 0) - (referralCashbackClaimed ?? 0)) / 20;
            const refereeCashback = ((sumTokenTracking?.referee_spend_mstr ?? 0) - (sumTokenTracking?.referee_claimed_mstr ?? 0)) / 10
            setTotalClaimedCashback((referralCashbackClaimed ?? 0) / 20 + (sumTokenTracking?.referee_claimed_mstr ?? 0) / 10)
            setTotalClaimableCashback(ownedCashback + refereeCashback);
            setMaxLevel(highestLevel);
            setTotal(total)
            if (commission) {
                setCommission(commission)
            }
            setLoadingReferee(false);
        })
            .catch(console.log)
            .finally(() => {
            });
    }, [page, limit, trigger]);

    const getReferralLink = () => {
        return `${SSO_META().DAOLink}/#/referral`;
    }
    const getEventLink = () => {
        return `${SSO_META().EventLink}/#/referral`;
    }
    const getOwnerLink = () => {
        const linkInvite = SSO_META().GameLink + `/#/?code=${utils.generateReferral(user?.[CustomerInterface.id])}`;
        return linkInvite
    }

    return (
        <div className="w-full mt-2 dao-container" id="resize-content">
            <Header className="text-center">
                <span className="text-white text-3xl font-semibold">
                    More friends, more fun {'&'} benefits
                </span>
            </Header>

            <div className="bg-black-secondary grid grid-cols-1 lg:flex w-full gap-8 p-8 border-grey-primary rounded-xl mt-8">
                <div className="text-[#8089A9] font-bold text-2xl col-span-1 lg:col-span-1 relative">
                    <img src="/assets/images/referral-banner.png" alt="cashback" className="object-cover w-full cursor-pointer"
                        onClick={() => {
                            window.open(`${getEventLink()}`, "_blank")
                        }}
                    />
                    <div className="absolute w-[110px] top-4 right-4">
                        <CustomButton color="yellow" className="py-1 text-lg" onClick={() => {
                            window.open(`${getEventLink()}`, "_blank")
                        }}>Detail</CustomButton>
                    </div>
                </div>
                <div className="bg-black-secondary grid grid-cols-1 lg:col-span-2 lg:grid-cols-2 w-full gap-8 border-grey-primary rounded-xl">
                    <div className="bg-black-linear relative">
                        <div className="flex justify-between px-6 pt-4 pb-2">
                            <div className="text-[#8089A9] font-bold text-lg">
                                Referral benefit
                            </div>
                            <div className="w-[120px] -translate-y-1">
                                <CustomButton color="blue"
                                    disabled={totalClaimableCashback <= 0 || isLoadingReferee}
                                    loading={loading}
                                    onClick={claimAllCashback}
                                >Claim</CustomButton>
                            </div>
                        </div>

                        <div className="px-6">
                            <div className="text-md text-white-light mt-4">
                                <div className="flex justify-between py-3">
                                    <div className="mt-1">
                                        <img
                                            src={`/assets/images/items/mstr-icon.svg`}
                                            alt="claim-reward"
                                            className="w-8 h-8 inline-block mr-1.5 mb-1"
                                        />
                                        Rewarded MSTR ready to claim:
                                    </div>
                                    {isLoadingReferee ? <div>
                                        <div className="loader-sm mx-auto"></div>
                                    </div> :
                                        <div className="font-cookie-run text-2xl">
                                            {utils.convertToInternationalCurrencySystem(totalClaimableCashback)}
                                        </div>}
                                </div>
                                <div className="flex justify-between pb-4">
                                    <div className="mt-1">
                                        <img
                                            src={`/assets/images/items/mstr-icon.svg`}
                                            alt="claim-reward"
                                            className="w-8 h-8 inline-block mr-1.5 mb-1"
                                        />
                                        Rewarded MSTR claimed:
                                    </div>
                                    {isLoadingReferee ? <div>
                                        <div className="loader-sm mx-auto"></div>
                                    </div> :
                                        <div className="font-cookie-run text-2xl">
                                            {utils.convertToInternationalCurrencySystem(totalClaimedCashback)}
                                        </div>}
                                </div>

                            </div>
                        </div>
                    </div>

                    <div className="bg-black-linear relative">
                        <div className="text-[#8089A9] font-bold text-lg px-6 py-4 flex justify-between">
                            My referral code
                            {/* <Popup
                                style={{ padding: "unset", border: "unset" }}
                                hoverable
                                position="bottom center"
                                trigger={ */}
                            <div className="text-white question-mark" onClick={() => window.open(getEventLink(), "_blank")}>
                                ?
                            </div>
                            {/* }
                                content={
                                    <div className="bg-white w-[360px] pb-6 bg-light-white px-4 absolute -translate-x-full rounded-lg">
                                        <div className="text-2xl text-left mt-4">
                                            For referrer:
                                        </div>
                                        {["10% commission on Referee's $MSTR spending ",
                                            "5% $MSTR commission on all Referee's marketplace transaction fee",
                                        ].map((rule, idx) => {
                                            return (
                                                <div className="text-left" key={`rule-${idx}`}>
                                                    - {rule}
                                                </div>
                                            )
                                        })}

                                    </div>
                                }
                                basic
                            /> */}
                        </div>
                        <div className="px-6 grid grid-cols-1 gap-4 pb-4 lg:pb-0 lg:absolute bottom-6 w-full">
                            <input
                                type={'text'}
                                className={"text-md block w-full h-[34px] font-medium bg-transparent text-white text-xl"}
                                disabled={true}
                                value={user?.[CustomerInterface.id] ? utils.generateReferral(user?.[CustomerInterface.id]) : ""}
                            />
                            <div className="flex justify-between gap-4 lg:col-span-2">
                                <CustomButton
                                    disabled={!user?.[CustomerInterface.id]}
                                    color="yellow"
                                    onClick={() => {
                                        if (!isCopy) {
                                            let text = `${utils.generateReferral(user?.[CustomerInterface.id])}`
                                            navigator.clipboard.writeText(text);
                                            setIsCopied(true);
                                            setTimeout(() => {
                                                setIsCopied(false);
                                            }, 2000)
                                        }
                                    }}
                                >
                                    {isCopy ? "Copied" : "Copy"}
                                </CustomButton>
                                <CustomButton
                                    disabled={!user?.[CustomerInterface.id]}
                                    color="yellow"
                                    onClick={() => {
                                        if (!isCopyLink) {
                                            const linkInvite = getOwnerLink();
                                            navigator.clipboard.writeText(linkInvite)
                                            setIsCopiedLink(true);
                                            setTimeout(() => {
                                                setIsCopiedLink(false);
                                            }, 2000)
                                        }
                                    }}
                                >
                                    {isCopyLink ? "Copied" : "Copy link"}
                                </CustomButton>


                                <CustomButton
                                    disabled={!user?.[CustomerInterface.id]}
                                    color="yellow"
                                    onClick={() => {
                                        openPopupShare(true);
                                    }}
                                >
                                    {"Share"}
                                </CustomButton>
                            </div>
                        </div>
                    </div>

                    <div className="bg-black-linear lg:col-span-2">
                        <div className="text-[#8089A9] font-bold text-lg px-6 py-4 flex justify-between">
                            <div className="flex gap-4">
                                <div>
                                    Enter referral code
                                </div>
                                <div className={`${user?.[CustomerInterface.referral_last_submit] ? "text-white" : "text-red"} text-right text-[14px]`}>
                                    {user?.[CustomerInterface.referral_last_submit] ?
                                        `(Submit in: ${dayjs(user?.[CustomerInterface.referral_last_submit]).format('DD/MM/YYYY')})`
                                        : `(Submit before: ${user?.[CustomerInterface.created_at] ?
                                            dayjs(user?.[CustomerInterface.created_at]).add(3, 'day').format('DD/MM/YYYY')
                                            : "DD/MM/YYYY"})`
                                    }
                                </div>
                            </div>
                            {/* <Popup
                                style={{ padding: "unset", border: "unset" }}
                                hoverable
                                position="bottom left"
                                trigger={ */}
                            <div className="text-white question-mark" onClick={() => window.open(getEventLink(), "_blank")}>
                                ?
                            </div>
                            {/* }
                                content={
                                    <div className="bg-white w-[380px] pb-6 bg-light-white px-4 absolute -translate-x-full rounded-lg">
                                        <div className="text-2xl text-left mt-4">
                                            For referee <span className="text-lg">(In 1st month from invitation)</span>:
                                        </div>
                                        {["5% cashback on total in-game $MSTR spending",
                                            "5% cashback from your own $MSTR marketplace transaction fee",
                                        ].map((rule, idx) => {
                                            return (
                                                <div className="text-left" key={`rule-popup-${idx}`}>
                                                    - {rule}
                                                </div>
                                            )
                                        })}

                                    </div>
                                }
                                basic
                            /> */}
                        </div>
                        <div className="px-6 py-4 bottom-4 flex justify-between gap-4">
                            <input
                                type={'text'}
                                className={
                                    `mb-4 md:mb-0 text-md block w-full h-[34px] font-medium bg-transparent rounded-sm text-white`
                                }
                                value={user?.[CustomerInterface.referral] ? utils.generateReferral(user?.[CustomerInterface.referral]) : referral}
                                disabled={!!user?.[CustomerInterface.referral]
                                    || (user?.[CustomerInterface.created_at]
                                        && dayjs().diff(user?.[CustomerInterface.created_at], 'day') >= 3)
                                }
                                placeholder="Enter code"
                                onChange={(e) => {
                                    setReferral(e.target.value);
                                }}
                            />
                            <CustomButton
                                disabled={!user?.[CustomerInterface.id]
                                    || !!user?.[CustomerInterface.referral]
                                    || (user?.[CustomerInterface.created_at]
                                        && dayjs().diff(user?.[CustomerInterface.created_at], 'day') >= 3)
                                    || !referral
                                }
                                color="yellow"
                                className="max-h-[35px] max-w-[140px]"
                                onClick={() => {
                                    submitReferral();
                                }}
                            >
                                Submit
                            </CustomButton>
                        </div>
                    </div>
                </div>

            </div >

            <div className="mt-7 mb-16">
                <Card
                    fluid
                    style={{ background: "transparent", boxShadow: "none" }}
                    description={
                        <div className="flex flex-col gap-4">
                            <div className="xl:flex xl:flex-row gap-6 ">
                                <div className="w-full xl:w-2/5 mb-10 xl:mb-0 bg-black-secondary rounded-xl">
                                    <div className="card-header text-white-light bg-gray py-3 px-6 rounded-t-xl">
                                        General Dashboard
                                    </div>

                                    <div className="text-white-light pb-3 pt-1 px-6 rounded-b-lg">
                                        <div className="flex justify-between py-5 text-md">
                                            <div className="mt-1 text-lg font-bold">
                                                <img
                                                    src={`/assets/images/items/star-icon.png`}
                                                    alt="friends"
                                                    className="w-8 h-8 inline-block mr-1.5 mb-1"
                                                />
                                                Commission level:
                                            </div>
                                            {isLoadingReferee ? <div className="h-min-content"><Loading /></div> : <div className="font-cookie-run text-2xl">{commisionData} %</div>}
                                        </div>
                                        <div className="br" />

                                        <div className="flex justify-between py-5 text-md">
                                            <div className="mt-1">
                                                <img
                                                    src={`/assets/images/items/mongen-icon.svg`}
                                                    alt="friends"
                                                    className="w-8 h-8 inline-block mr-1.5 mb-1"
                                                />
                                                Referred Friends
                                            </div>
                                            {isLoadingReferee ? <div className="h-min-content"><Loading /></div> : <div className="font-cookie-run text-2xl">{total}</div>}
                                        </div>
                                        <div className="br" />

                                        <div className="flex justify-between py-5 text-md">
                                            <div className="mt-1">
                                                <img
                                                    src={`/assets/images/items/mongen-face-icon.svg`}
                                                    alt="highest-level"
                                                    className="w-8 h-8 inline-block mr-1.5"
                                                />
                                                Highest Level Friends
                                            </div>
                                            {isLoadingReferee ? <div className="h-min-content"><Loading /></div> : <div className="font-cookie-run text-2xl">{maxLevel}</div>}
                                        </div>
                                        <div className="br" />

                                        <div className="flex justify-between py-5 text-md">
                                            <div className="mt-1">
                                                <img
                                                    src={`/assets/images/items/claim-box-icon.svg`}
                                                    alt="claim-reward"
                                                    className="w-8 h-8 inline-block mr-1.5 mb-1"
                                                />
                                                Claimed Rewards
                                            </div>
                                            <div className="font-cookie-run text-2xl">
                                                {user ? user?.["AF"] : 0}
                                            </div>
                                        </div>
                                        <div className="br" />
                                    </div>
                                </div>

                                <div className="w-full bg-black-secondary rounded-xl">
                                    <div className="card-header text-white-light bg-gray py-3 px-6 rounded-t-xl">
                                        Friends list
                                    </div>

                                    <div className="text-white-light rounded-lg pb-10">
                                        <div>
                                            <CustomTable
                                                className="min-h-[250px] max-h-[250px] overflow-y-scroll"
                                                tableHeader={tableHeader}
                                                tableData={referredCustomers}
                                                overrideHeader=""
                                                loading={isLoadingReferee}
                                            />
                                            {total / limit > 1 && (
                                                <Pagination
                                                    defaultActivePage={page}
                                                    totalPages={Math.ceil(total / limit)}
                                                    onPageChange={(event: any, pageInfo) => {
                                                        setPage(Number(pageInfo.activePage));
                                                    }}
                                                    firstItem={null}
                                                    lastItem={null}
                                                    className="bg-black-secondary"
                                                    style={{ float: "right" }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                />
            </div>

            <Modal
                open={popupShare}
                onClose={() => openPopupShare(false)}
                style={{ width: "unset" }}
                className="w-[530px] lg:hidden block w-full">
                <div className="max-w-[530px] w-screen max-h-full bg-black-primary px-8 z-[20] py-3 w-full"
                    style={{ border: "0.1px solid #0F1011", transform: "scale(1)" }}>
                    <div className="text-white text-2xl cursor-pointer right-3 top-3 absolute" onClick={() => openPopupShare(false)}>
                        <img src="/assets/images/icons/close-icon.svg" alt="close" className="" />
                    </div>
                    <div className="pb-4">
                        <div className="text-center text-white text-xl py-6 font-extrabold main-font-bold">
                            Share with
                        </div>
                        <img src="/assets/images/hr.svg" alt="hr" className="w-full object-cover h-1 mb-9" />
                        <div className="w-full mx-auto">
                            <div className="text-center text-white grid grid-cols-3 gap-1 mx-auto w-full text-center px-6">
                                <div className="w-full text-center mx-auto">
                                    <FacebookShareButton
                                        className="cursor-pointer"
                                        url={`${getOwnerLink()}`}
                                        quote={`Easy playing, easy earning! \n🔥Play Monsterra NFT Game - top #NFTGame on #BNBChain #Gamefi now at: ${getOwnerLink()}\nPlay for Free NOW by claiming free Land and Mongen eggs!!`}
                                    >
                                        <img src="/assets/images/social/facebook.svg" alt="hr" className="social-icon cursor-pointer mx-auto text-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] object-cover h-1 mb-4" />
                                    </FacebookShareButton>
                                    <div>Facebook</div>
                                </div>
                                <div className="w-full text-center mx-auto">
                                    <TwitterShareButton
                                        className="cursor-pointer"
                                        url={" "}
                                        // title={`🔥Play @Monsterra_P2E for FREE and EARN now at: ${getOwnerLink()}\n👉Or apply my code: ${utils.generateReferral(user?.[CustomerInterface.id])} in ${getReferralLink()} to share huge earning daily together!`}
                                        title={`🔥Play @Monsterra_P2E - top #NFTGame on #BNBChain #Gamefi now at: ${getOwnerLink()}\nLet's play with me to share huge earning daily together! \n👉 Join to get free Land and Mongen eggs!!`}
                                    >
                                        <img src="/assets/images/social/twitter.svg" alt="hr" className="social-icon cursor-pointer mx-auto text-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] object-cover h-1 mb-4" />
                                    </TwitterShareButton>
                                    <div>Twitter</div>
                                </div>
                                <div className="w-full text-center mx-auto">
                                    <TelegramShareButton
                                        url={` `}
                                        // title={
                                        //     `Easy playing, easy earning in Top #GameFi Project on #BNB Chain! \n🔥Play #<a href= ${Config.Social.HomeTelegram}>Monsterra</a> for FREE and EARN now at: ${getOwnerLink()}\n👉Or apply my code: ${utils.generateReferral(user?.[CustomerInterface.id])} in ${getReferralLink()} to share huge earning daily together! \nJoin to get free Land and Mongen eggs!! Let’s discuss with big Monsterra community 👇\n🌟 <a href=${Config.Social.Web}>Website</a> | <a href=${Config.Social.Twitter}>Twitter</a> | <a href=${Config.Social.Discord}>Discord</a> | <a href=${Config.Social.Facebook}>Facebook</a> | <a href=${Config.Social.Telegram}>Telegram Channel</a> | <a href=${Config.Social.Youtube}>Youtube</a> | <a href=${Config.Social.Facebook}>TikTok</a>`
                                        // }
                                        title={`Easy playing, easy earning! \n🔥Play #<a href= ${Config.Social.HomeTelegram}>Monsterra_P2E</a> - top #NFTGame on #BNBChain #Gamefi now at: ${getOwnerLink()}\nPlay for Free NOW by claiming free Land and Mongen eggs!!\nLet’s discuss with big Monsterra community 👇\n🌟 <a href=${Config.Social.Web}>Website</a> | <a href=${Config.Social.Twitter}>Twitter</a> | <a href=${Config.Social.Discord}>Discord</a> | <a href=${Config.Social.Facebook}>Facebook</a> | <a href=${Config.Social.Telegram}>Telegram Channel</a> | <a href=${Config.Social.Youtube}>Youtube</a> | <a href=${Config.Social.Facebook}>TikTok</a>`}
                                    >
                                        <img src="/assets/images/social/telegram.svg" alt="hr" className="social-icon cursor-pointer mx-auto text-center w-[60px] h-[60px] md:w-[80px] md:h-[80px] object-cover h-1 mb-4" />
                                    </TelegramShareButton>
                                    <div>Telegram</div>
                                </div>
                            </div>
                        </div>
                        <div className="text-[#6D6D76] mt-8 text-center w-full">
                            Or share with link
                        </div>
                        <div className="pt-4 pb-2 px-3 bg-[#29292E] text-white flex justify-between mt-3">
                            <div className="truncate ...">{`${getOwnerLink()}`}</div>
                            <div className="-translate-y-1">
                                <img src='/assets/images/icons/copy-icon.svg' alt="copy" className="cursor-pointer" onClick={() => {
                                    const linkInvite = `${getOwnerLink()}`;
                                    navigator.clipboard.writeText(linkInvite)
                                }} />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal >
        </div >
    );
}
